/**
 * RCA Data Hooks
 * Project Risk & Complexity Assessment
 *
 * TanStack Query hooks wrapping the supabaseService functions.
 * Provides data fetching, caching, and mutation with automatic
 * query invalidation.
 *
 * @version 2.0.0
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  listProjects,
  listAllAssessments,
  saveProject,
  listAssessments,
  saveAssessment,
  saveShareToken,
  validateShareToken,
  listDocuments,
  getDocument,
} from '../services/supabaseService';
import type {
  RCAProject,
  AssessmentSummary,
  SupabaseAssessment,
  ShareToken,
  RCACategory,
  RiskLevel,
  ProjectDocument,
} from '../types/rca';

// ============================================================
// Query Keys
// ============================================================

export const QUERY_KEYS = {
  projects: ['projects'] as const,
  allAssessments: ['assessments', 'all'] as const,
  assessments: (projectId: string) => ['assessments', projectId] as const,
  shareToken: (token: string) => ['shareToken', token] as const,
  documents: (projectId: string) => ['documents', projectId] as const,
  document: (id: string) => ['document', id] as const,
} as const;

// ============================================================
// Projects
// ============================================================

/**
 * Fetch all projects.
 * Stale after 60 seconds; refetches on window focus.
 */
export function useProjects(): UseQueryResult<RCAProject[], Error> {
  return useQuery({
    queryKey: QUERY_KEYS.projects,
    queryFn: listProjects,
    staleTime: 60_000,
  });
}

export type EnrichedProject = RCAProject & {
  risk_level: RiskLevel | null;
  complexity_level: RiskLevel | null;
  category: RCACategory | null;
  avg_risk: number | null;
  avg_complexity: number | null;
};

/**
 * Fetch all projects enriched with their latest assessment scores.
 * Runs two parallel queries (projects + all assessments) and merges by project_id.
 */
export function useProjectsWithScores(): UseQueryResult<EnrichedProject[], Error> {
  return useQuery({
    queryKey: [...QUERY_KEYS.projects, 'enriched'],
    queryFn: async (): Promise<EnrichedProject[]> => {
      const [projects, assessments] = await Promise.all([
        listProjects(),
        listAllAssessments(),
      ]);
      const latestByProject = new Map<string, AssessmentSummary>();
      for (const a of assessments) {
        const existing = latestByProject.get(a.project_id);
        if (!existing || a.updated_at > existing.updated_at) {
          latestByProject.set(a.project_id, a);
        }
      }
      return projects.map((p) => {
        const a = latestByProject.get(p.id) as
          | (AssessmentSummary & { risk_score?: number; complexity_score?: number })
          | undefined;
        return {
          ...p,
          risk_level: (a?.risk_level as RiskLevel) ?? null,
          complexity_level: (a?.complexity_level as RiskLevel) ?? null,
          category: (a?.category as RCACategory) ?? null,
          avg_risk: a?.risk_score ?? a?.avg_risk ?? null,
          avg_complexity: a?.complexity_score ?? a?.avg_complexity ?? null,
        };
      });
    },
    staleTime: 60_000,
  });
}

/**
 * Mutation to create or update a project.
 * Invalidates the projects list on success.
 */
export function useSaveProject(): UseMutationResult<
  RCAProject,
  Error,
  Partial<RCAProject>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (project: Partial<RCAProject>) => saveProject(project),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
    },
  });
}

// ============================================================
// Assessments
// ============================================================

/**
 * Fetch all assessments for a specific project.
 * Stale after 60 seconds. Disabled if no projectId provided.
 *
 * @param projectId - UUID of the project whose assessments to fetch
 */
export function useAssessments(
  projectId: string
): UseQueryResult<AssessmentSummary[], Error> {
  return useQuery({
    queryKey: QUERY_KEYS.assessments(projectId),
    queryFn: () => listAssessments(projectId),
    staleTime: 60_000,
    enabled: Boolean(projectId),
  });
}

/**
 * Mutation to create or update an assessment.
 * Invalidates the assessments list for the relevant project on success.
 */
export function useSaveAssessment(): UseMutationResult<
  SupabaseAssessment,
  Error,
  Partial<SupabaseAssessment>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assessment: Partial<SupabaseAssessment>) =>
      saveAssessment(assessment),
    onSuccess: (data) => {
      if (data.project_id) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.assessments(data.project_id),
        });
      }
    },
  });
}

// ============================================================
// Share Tokens
// ============================================================

interface GenerateShareTokenInput {
  assessmentId: string;
  expiresAt: string;
}

/**
 * Mutation to generate a share token for an assessment.
 */
export function useGenerateShareToken(): UseMutationResult<
  ShareToken,
  Error,
  GenerateShareTokenInput
> {
  return useMutation({
    mutationFn: ({ assessmentId, expiresAt }: GenerateShareTokenInput) =>
      saveShareToken(assessmentId, expiresAt),
  });
}

// ============================================================
// Documents
// ============================================================

export function useDocuments(
  projectId: string
): UseQueryResult<ProjectDocument[], Error> {
  return useQuery({
    queryKey: QUERY_KEYS.documents(projectId),
    queryFn: () => listDocuments(projectId),
    staleTime: 60_000,
    enabled: Boolean(projectId),
  });
}

export function useDocument(
  id: string
): UseQueryResult<ProjectDocument | null, Error> {
  return useQuery({
    queryKey: QUERY_KEYS.document(id),
    queryFn: () => getDocument(id),
    staleTime: 60_000,
    enabled: Boolean(id),
  });
}

/**
 * Validate a share token and retrieve the associated assessment.
 * Stale after 60 seconds. Disabled if no token provided.
 *
 * @param token - Share token string
 */
export function useValidateShareToken(
  token: string
): UseQueryResult<SupabaseAssessment | null, Error> {
  return useQuery({
    queryKey: QUERY_KEYS.shareToken(token),
    queryFn: () => validateShareToken(token),
    staleTime: 60_000,
    enabled: Boolean(token),
  });
}
